import React from "react";
//#region types
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useCustomerSearch } from "components/customer";
import { useBusinessUnitAutoComplete } from "components/businessUnit";
import { useCompanyService, useCustomerService, useBusinessUnitService } from "serviceHooks";
//#endregion
import { QueryStateHelper } from "utils/helpers";

function useTimeReportingProjectReportFilterDefinition() {
  const companyService = useCompanyService();
  const customerService = useCustomerService();
  const businessUnitService = useBusinessUnitService();
  const searchCustomers = useCustomerSearch();
  const businessUnitAutoComplete = useBusinessUnitAutoComplete();

  const [suggestedCustomers, setSuggestedCustomers] = React.useState<AutocompleteListItem[]>([]);

  React.useEffect(() => {
    customerService
      .getCustomerSuggestions()
      .then((res) =>
        setSuggestedCustomers(res.map((customer: any) => ({ id: customer.id ?? 0, value: customer.name ?? "" })))
      );
  }, []);

  const filterFormFields: GenericFormSection<TimeReportingProjectFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        period: {
          label: TranslationKeys.Common.Date,
          type: FormEnums.FormFieldType.MONTH,
        },
        customerName: {
          label: TranslationKeys.Invoice.Customer,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReportingProject.Filter.CustomerId,
            suggestedItems: suggestedCustomers,
            items: searchCustomers,
          } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.Filter>,
        },
        projectMemberName: {
          label: TranslationKeys.Common.Users,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WUser,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReportingProject.Filter.ProjectMemberId,
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.Filter>,
        },
        businessUnitName: {
          label: TranslationKeys.BusinessUnit.BusinessUnitName,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WCustomer,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReportingProject.Filter.BusinessUnitId,
            items: (searchStr: string) => businessUnitAutoComplete(searchStr),
          } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.Filter>,
        },
        onlyWithLeftToInvoice: {
          label: TranslationKeys.TimeReportingProject.OnlyWithLeftToInvoice,
          type: FormEnums.FormFieldType.TOGGLE,
        },
        includeDeletedProjects: {
          label: TranslationKeys.TimeReportingProject.IncludeDeletedProjects,
          type: FormEnums.FormFieldType.TOGGLE,
        },
        includeInactiveProjects: {
          label: TranslationKeys.TimeReportingProject.IncludeInactiveProjects,
          type: FormEnums.FormFieldType.TOGGLE,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<TimeReportingProjectFormTypes.Filter> = {
    projectName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.TimeReportingProject.Filter.ProjectName),
    period: QueryStateHelper.monthQueryEncoder(FormFieldConstants.TimeReportingProject.Filter.Period),
    customerId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.TimeReportingProject.Filter.CustomerId),
    customerName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.TimeReportingProject.Filter.CustomerName),
    projectMemberId: QueryStateHelper.numberQueryEncoder(
      FormFieldConstants.TimeReportingProject.Filter.ProjectMemberId
    ),
    projectMemberName: QueryStateHelper.stringQueryEncoder(
      FormFieldConstants.TimeReportingProject.Filter.ProjectMemberName
    ),
    businessUnitId: QueryStateHelper.stringQueryEncoder(FormFieldConstants.TimeReportingProject.Filter.BusinessUnitId),
    businessUnitName: QueryStateHelper.stringQueryEncoder(
      FormFieldConstants.TimeReportingProject.Filter.BusinessUnitName
    ),
    onlyWithLeftToInvoice: QueryStateHelper.booleanQueryEncoder(
      FormFieldConstants.TimeReportingProject.Filter.OnlyWithLeftToInvoice
    ),
    includeDeletedProjects: QueryStateHelper.booleanQueryEncoder(
      FormFieldConstants.TimeReportingProject.Filter.IncludeDeletedProjects
    ),
    includeInactiveProjects: QueryStateHelper.booleanQueryEncoder(
      FormFieldConstants.TimeReportingProject.Filter.IncludeInactiveProjects
    ),
  };

  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModelTimeReporting.TimeReportingProjectFilter) => {
    const reportFilter: TimeReportingProjectFormTypes.Filter = {};

    if (filter.projectName !== undefined) {
      reportFilter.projectName = filter.projectName;
    }

    if (filter.from !== undefined || filter.to !== undefined) {
      const period = filter.from ?? filter.to;
      reportFilter.period = new Date(period!);
    }

    if (filter.customerId !== undefined) {
      reportFilter.customerId = filter.customerId;
      reportFilter.customerName = await customerService.getCustomer(filter.customerId).then((c) => c.name ?? "");
    }

    if (filter.projectMemberId !== undefined) {
      reportFilter.projectMemberId = filter.projectMemberId;
      reportFilter.projectMemberName = await companyService
        .getEmployees({ personIds: [filter.projectMemberId] })
        .then((res) => res[0]?.name ?? "");
    }

    if (filter.businessUnitId !== undefined) {
      reportFilter.businessUnitId = filter.businessUnitId;
      reportFilter.businessUnitName = await businessUnitService
        .getBusinessUnit(filter.businessUnitId)
        .then((bu) => bu.name ?? "");
    }

    if (filter.onlyWithLeftToInvoice !== undefined) {
      reportFilter.onlyWithLeftToInvoice = filter.onlyWithLeftToInvoice;
    }

    if (filter.includeDeletedProjects !== undefined) {
      reportFilter.includeDeletedProjects = filter.includeDeletedProjects;
    }

    if (filter.includeInactiveProjects !== undefined) {
      reportFilter.includeInactiveProjects = filter.includeInactiveProjects;
    }

    return reportFilter;
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useTimeReportingProjectReportFilterDefinition;
